/**
 * Clicking a gallery photo/thumbnail that is linked to a specific variant
 * (e.g. a color swatch's own photo) also selects that variant, so the
 * variant picker stays in sync with whichever photo the shopper is looking at.
 */
function handleClick(event) {
  const mediaEl = event.target.closest('[data-media-id]');
  if (!mediaEl) return;

  const gallery = mediaEl.closest('media-gallery');
  if (!gallery) return;

  const mapScript = gallery.querySelector('script[data-gallery-variant-map]');
  if (!mapScript) return;

  let map;
  try {
    map = JSON.parse(mapScript.textContent);
  } catch {
    return;
  }

  const variantId = map[mediaEl.dataset.mediaId];
  if (!variantId) return;

  const section = gallery.closest('.shopify-section, dialog');
  const radio = section?.querySelector(`variant-picker input[data-variant-id="${variantId}"]`);
  if (!radio || radio.checked) return;

  radio.checked = true;
  radio.dispatchEvent(new Event('change', { bubbles: true }));
}

document.addEventListener('click', handleClick);

/**
 * On the initial page load (not on the fetch-driven re-renders that follow a
 * variant click), show the cover slide with no color swatch marked as chosen.
 * The underlying add-to-cart variant id is untouched, so "Add to Cart" still
 * works even if the shopper never clicks a swatch — only the visual selection
 * and the gallery's starting slide are reset.
 *
 * This only runs once: the fetched HTML that replaces the gallery/variant
 * picker after a click is parsed with DOMParser, which never executes the
 * <script> tags it contains, and this file is loaded as a module (fetched
 * and evaluated once per page).
 */
function clearDefaultVariantSelection(gallery) {
  const section = gallery.closest('.shopify-section, dialog');
  const picker = section?.querySelector('variant-picker');
  if (!picker) return;

  picker.querySelectorAll('input[type="radio"]').forEach((input) => {
    input.checked = false;
    input.dataset.currentChecked = 'false';
    input.dataset.previousChecked = 'false';
  });

  picker.classList.add('variant-picker--pending-selection');
  const endSticky = setStickyPending(section, gallery, picker);
  picker.addEventListener(
    'change',
    () => {
      picker.classList.remove('variant-picker--pending-selection');
      endSticky?.();
    },
    { once: true }
  );
}

/**
 * Keep the sticky add-to-cart bar in step with the "nothing chosen yet" state:
 * until the shopper picks an option it shows the cover photo and "Choose a colour",
 * and its button scrolls up to the options instead of silently adding the default.
 * Products with a single variant are left alone.
 * Returns a function that restores the bar once a real choice is made.
 */
function setStickyPending(section, gallery, picker) {
  const sticky = section?.querySelector('sticky-add-to-cart');
  const info = sticky?.querySelector('.sticky-add-to-cart__info');
  if (!sticky || !info || info.dataset.oneVariant === 'true') return null;

  const variantEl = sticky.querySelector('.sticky-add-to-cart__variant');
  const imageEl = sticky.querySelector('.sticky-add-to-cart__image img');
  const button = sticky.querySelector('.sticky-add-to-cart__button');
  const coverImg = gallery.querySelector('img');
  const optionName = picker.querySelector('input[type="radio"]')?.dataset.optionName || '';
  const label = /^colou?r$/i.test(optionName) ? 'Choose colour' : `Choose ${optionName.toLowerCase() || 'option'}`;
  const buttonTextEl = button?.querySelector('.add-to-cart-text__content > span');

  const saved = {
    buttonText: buttonTextEl?.textContent,
    text: variantEl?.textContent,
    src: imageEl?.getAttribute('src'),
    srcset: imageEl?.getAttribute('srcset'),
  };

  sticky.dataset.pendingSelection = 'true';
  if (buttonTextEl) buttonTextEl.textContent = label;
  if (imageEl && coverImg) {
    imageEl.removeAttribute('srcset');
    imageEl.src = coverImg.currentSrc || coverImg.src;
  }

  const goToOptions = (event) => {
    if (sticky.dataset.pendingSelection !== 'true') return;
    if (!button || !(event.target instanceof Node) || !button.contains(event.target)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    picker.classList.add('variant-picker--nudge');
    setTimeout(() => picker.classList.remove('variant-picker--nudge'), 1200);
  };
  window.addEventListener('click', goToOptions, true);

  return () => {
    delete sticky.dataset.pendingSelection;
    window.removeEventListener('click', goToOptions, true);
    // Restore the default variant's details; the theme overwrites them with the newly chosen option.
    if (buttonTextEl && saved.buttonText != null) buttonTextEl.textContent = saved.buttonText;
    if (variantEl && saved.text != null) variantEl.textContent = saved.text;
    if (imageEl && saved.src) {
      imageEl.src = saved.src;
      if (saved.srcset) imageEl.setAttribute('srcset', saved.srcset);
    }
  };
}

function forceGalleryToCover(gallery) {
  const trySelect = () => {
    const slideshow = gallery.slideshow;
    if (!slideshow || !slideshow.refs?.slides?.length) {
      requestAnimationFrame(trySelect);
      return;
    }
    if (slideshow.current !== 0) {
      slideshow.select(0, undefined, { animate: false });
    }
  };

  // Let the slideshow's own setup (including its deferred initial-slide scroll) finish first.
  setTimeout(trySelect, 150);
}

document.querySelectorAll('media-gallery').forEach((gallery) => {
  clearDefaultVariantSelection(gallery);
  forceGalleryToCover(gallery);
});

/**
 * Workaround for a bug in the theme's own slideshow-component: when a fresh
 * gallery is inserted (e.g. after a variant click replaces the whole
 * <media-gallery>), its setup code sets `current` to the target slide index
 * *before* calling select() to actually scroll there — so select()'s "already
 * on this slide" guard sees current === target and bails out without ever
 * scrolling, leaving the wrong photo on screen while the swatch above it
 * shows the correct color as selected.
 *
 * Rather than patch the theme's own slideshow.js (edits to it don't reliably
 * take effect — this file's edits do), force the scroll here instead: after
 * any new <media-gallery> appears, if its slideshow's `current` doesn't match
 * the slide whose id equals the selected variant's media, jump there directly.
 */
function fixStaleSlidePosition(gallery) {
  const trySelect = () => {
    const slideshow = gallery.slideshow;
    const slides = slideshow?.refs?.slides;
    if (!slideshow || !slides?.length) {
      requestAnimationFrame(trySelect);
      return;
    }

    const mapScript = gallery.querySelector('script[data-gallery-variant-map]');
    if (!mapScript) return;

    let map;
    try {
      map = JSON.parse(mapScript.textContent);
    } catch {
      return;
    }

    const section = gallery.closest('.shopify-section, dialog');
    const checkedRadio = section?.querySelector('variant-picker input:checked');
    if (!checkedRadio) return;

    const targetMediaId = Object.keys(map).find((mediaId) => map[mediaId] === checkedRadio.dataset.variantId);
    if (!targetMediaId) return;

    const targetIndex = slides.findIndex((slide) => slide.getAttribute('slide-id') === targetMediaId);
    if (targetIndex === -1 || targetIndex === slideshow.current) return;

    const scroller = gallery.querySelector('[ref="scroller"]');
    const before = scroller?.scrollLeft;
    slideshow.select(targetIndex, undefined, { animate: false });

    // If the guard bug swallowed the scroll (current changes but scrollLeft doesn't), force it directly.
    requestAnimationFrame(() => {
      if (scroller && before === scroller.scrollLeft) {
        const slide = slides[targetIndex];
        if (slide) scroller.scrollLeft = slide.offsetLeft;
      }
    });
  };

  // Run as soon as possible: connectedCallback (where the buggy `current` pre-set
  // happens) runs synchronously before this MutationObserver callback fires, so
  // `slideshow.refs.slides` is already populated and there's nothing to wait for.
  // The theme's own deferred correction attempt is itself swallowed by the same
  // guard bug, so it can never race with or override this fix — no delay needed.
  trySelect();
}

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType !== 1) continue;
      if (node.tagName === 'MEDIA-GALLERY') fixStaleSlidePosition(node);
      node.querySelectorAll?.('media-gallery').forEach(fixStaleSlidePosition);
    }
  }
}).observe(document.body, { childList: true, subtree: true });
